<template>
  <Stack gap="2">
    <div class="stk-p-6">
      <Level id="cases-header" class="stk-mb-6">
        <LevelLeft>
          <Title size="7">
            Cases
          </Title>
        </LevelLeft>
        <LevelRight>
          <Buttons>
            <Button
              variant="primary"
              icon-after="fas fa-plus"
              :disabled="isLoadingCases"
              size="3_5"
              @click="startNewCase"
            >
              <span>Create case</span>
            </Button>
          </Buttons>
        </LevelRight>
      </Level>
      <div class="cases-controls">
        <form
          class="search-form stk-resize-3_5"
          @submit.prevent="handleSearch"
        >
          <input
            v-model="searchQuery"
            type="text"
            class="stk-input search-input"
            placeholder="Search by subject or case number"
            :disabled="isLoadingCases"
          >
          <Button type="primary" :disabled="isLoadingCases">
            Search
          </Button>
          <Button type="button" :disabled="isLoadingCases" @click="handleClear">
            Clear
          </Button>
        </form>
        <div class="cases-filters">
          <select
            v-model="selectedStatus"
            class="stk-input case-filter-select"
            :disabled="isLoadingCases"
          >
            <option value="">All statuses</option>
            <option v-for="statusOption in statusOptions" :value="statusOption.value">
              {{ statusOption.label }}
            </option>
          </select>
          <select
            v-model="selectedPriority"
            class="stk-input case-filter-select"
            :disabled="isLoadingCases"
          >
            <option value="">All priorities</option>
            <option v-for="priorityOption in priorityOptions" :value="priorityOption">
              {{ priorityOption }}
            </option>
          </select>
        </div>
        <div class="cases-actions">
          <Button
            type="secondary"
            icon-before="fas fa-redo-alt"
            :disabled="isLoadingCases"
            title="Refresh"
            @click="fetchCases"
          />
          <Button
            type="secondary"
            :disabled="isLoadingCases || isLoadingCasePeopleReview || isApplyingCasePeopleReview"
            @click="openCasePeopleReviewModal"
          >
            {{ isCasePeopleApplyTaskRunning ? 'Case Linking Status' : 'Link Cases' }}
          </Button>
        </div>
      </div>
    </div>
    <StakeModal v-model="showCasePeopleReviewModal" :width="900">
      <Stack gap="2" class="stk-p-4">
        <Title>Review Case Linking</Title>
        <p v-if="!isCasePeopleApplyTaskRunning">
          Review the proposed linking and creation plan before applying changes. You can exclude specific people or cases.
        </p>
        <p v-if="isCasePeopleApplyTaskRunning">
          Case linking is in progress. Progress updates are shown below.
        </p>
        <p v-else-if="isLoadingCasePeopleReview">
          Loading review details...
        </p>
        <p v-if="casePeopleReviewError" class="review-error">
          {{ casePeopleReviewError }}
        </p>
        <p v-if="!isCasePeopleApplyTaskRunning && !isLoadingCasePeopleReview && !casePeopleReviewError" class="review-summary">
          {{ selectedReviewCasesCount }} selected cases across {{ selectedReviewPeopleCount }} selected people
          ({{ reviewSummary.actionable_cases }} actionable cases total).
        </p>
        <div v-if="isCasePeopleApplyTaskRunning" class="review-task-progress">
          <p class="review-task-progress-title">
            {{ casePeopleApplyTaskMeta.status_message || 'Running...' }}
          </p>
          <p class="review-task-progress-item">
            Entities created and linked: {{ Number(casePeopleApplyTaskMeta.entities_created_and_linked || 0) }}
          </p>
          <p class="review-task-progress-item">
            Entities to be created: {{ Number(casePeopleApplyTaskMeta.entities_to_create || 0) }}
          </p>
          <p class="review-task-progress-item">
            Cases linked to entities: {{ Number(casePeopleApplyTaskMeta.cases_linked_to_entities || 0) }}
          </p>
        </div>
        <div v-if="!isCasePeopleApplyTaskRunning && !isLoadingCasePeopleReview && !casePeopleReviewError && hasReviewActionableItems" class="review-bulk-actions">
          <div class="review-filter-controls">
            <input
              v-model="reviewSearchQuery"
              type="text"
              class="stk-input review-search-input"
              placeholder="Search people or cases"
              :disabled="isApplyingCasePeopleReview || isCasePeopleApplyTaskRunning"
            >
            <p class="review-filter-label">Filter by</p>
            <Buttons class="review-filter-buttons">
              <Button
                class="review-filter-button"
                :variant="selectedReviewCaseFilter === 'all' ? 'primary' : 'secondary'"
                :disabled="isApplyingCasePeopleReview || isCasePeopleApplyTaskRunning"
                @click="selectedReviewCaseFilter = 'all'"
              >
                All
              </Button>
              <Button
                class="review-filter-button"
                :variant="selectedReviewCaseFilter === 'link_existing' ? 'primary' : 'secondary'"
                :disabled="isApplyingCasePeopleReview || isCasePeopleApplyTaskRunning"
                @click="selectedReviewCaseFilter = 'link_existing'"
              >
                Will just link
              </Button>
              <Button
                class="review-filter-button"
                :variant="selectedReviewCaseFilter === 'create_missing' ? 'primary' : 'secondary'"
                :disabled="isApplyingCasePeopleReview || isCasePeopleApplyTaskRunning"
                @click="selectedReviewCaseFilter = 'create_missing'"
              >
                Will create person and link
              </Button>
            </Buttons>
          </div>
          <div class="review-select-all-row">
            <Button
              type="secondary"
              :disabled="isApplyingCasePeopleReview || isCasePeopleApplyTaskRunning"
              @click="toggleSelectAllReview"
            >
              {{ areAllReviewCasesSelected ? 'Unselect All' : 'Select All' }}
            </Button>
          </div>
        </div>
        <section v-if="!isCasePeopleApplyTaskRunning && !isLoadingCasePeopleReview && !casePeopleReviewError" class="review-panel">
          <p v-if="filteredReviewPeople.length === 0" class="review-empty">No people require linking or creation.</p>
          <div v-for="person in filteredReviewPeople" class="review-person-row">
            <label class="review-checkbox-label">
              <template v-if="person.action === 'create_missing'">
                <input
                  type="checkbox"
                  :checked="person.selected"
                  :disabled="isCasePeopleApplyTaskRunning"
                  @change="toggleReviewPerson(person.person_key, $event.target.checked)"
                >
              </template>
              <span class="review-person-name">{{ person.display_name || person.email || 'Unknown person' }}</span>
            </label>
            <span class="review-action-pill">
              {{ person.action === 'create_missing' ? (person.selected ? 'Create Person + Link Case' : 'Not Creating') : 'Link Existing' }}
            </span>
            <p v-if="person.email" class="review-person-email">{{ person.email }}</p>
            <p class="review-person-cases">{{ getCasesForPerson(person).length }} case(s)</p>

            <div class="review-cases-under-person">
              <div v-for="caseReview in getCasesForPerson(person)" class="review-case-row">
                <label class="review-checkbox-label">
                  <input
                    type="checkbox"
                    :checked="caseReview.selected"
                    :disabled="isCasePeopleApplyTaskRunning || !canToggleCaseForPerson(person, caseReview)"
                    @change="toggleReviewCase(caseReview.case_id, $event.target.checked)"
                  >
                  <span class="review-case-subject">
                    {{ caseReview.subject || caseReview.case_number || caseReview.case_id }}
                  </span>
                </label>
                <p class="review-case-meta">
                  {{ caseReview.case_number || 'No case number' }} · {{ caseReview.status || 'Unknown status' }}
                </p>
                <p class="review-case-reason">{{ caseReview.proposed_reason }}</p>
              </div>
            </div>
          </div>
        </section>
        <p v-if="casePeopleApplyError" class="review-error">
          {{ casePeopleApplyError }}
        </p>
        <Buttons>
          <Button :disabled="isApplyingCasePeopleReview || isCasePeopleApplyTaskRunning" @click="closeCasePeopleReviewModal">
            Cancel
          </Button>
          <Button
            type="secondary"
            :disabled="isLoadingCasePeopleReview || isApplyingCasePeopleReview || isCasePeopleApplyTaskRunning || !hasReviewActionableItems || selectedReviewCasesCount === 0"
            @click="applyCasePeopleReviewSelections"
          >
            {{ isApplyingCasePeopleReview ? 'Starting...' : (isCasePeopleApplyTaskRunning ? 'Running...' : 'Apply Selected Changes') }}
          </Button>
        </Buttons>
      </Stack>
    </StakeModal>
    <p v-if="showFilteredCount" class="cases-filtered-count stk-pl-6">
      Showing {{ cases.length }} case<span v-if="cases.length !== 1">s</span>
      <span v-if="isUsingSavedFilter" class="cases-saved-filter-indicator">
        Using saved filters,
        <a href="#" class="cases-saved-filter-link" @click.prevent="handleClear">click here</a>
        to clear filters
      </span>
    </p>

    <LoaderWithText class="stk-pl-6" v-if="isLoadingCases">Loading cases…</LoaderWithText>
    <div v-else-if="cases.length === 0" class="stk-pl-6 empty-state">
      No cases yet.
    </div>
    <template v-else>
      <table class="cases-table">
      <thead class="cases-head">
        <tr>
          <th class="cases-col-head cell-subject">Subject</th>
          <th class="cases-col-head is-sortable" :class="{ 'is-active': sortBy === 'client' }">
            <button
              class="cases-col-head-button"
              type="button"
              @click="toggleSort('client')"
            >
              <span class="cases-col-head-label">Client</span>
              <Icon class="cases-col-head-icon" :icon="getSortIcon('client')" />
            </button>
          </th>
          <th class="cases-col-head">Case Number</th>
          <th class="cases-col-head is-sortable" :class="{ 'is-active': sortBy === 'status' }">
            <button
              class="cases-col-head-button"
              type="button"
              @click="toggleSort('status')"
            >
              <span class="cases-col-head-label">Status</span>
              <Icon class="cases-col-head-icon" :icon="getSortIcon('status')" />
            </button>
          </th>
          <th class="cases-col-head is-sortable" :class="{ 'is-active': sortBy === 'priority' }">
            <button
              class="cases-col-head-button"
              type="button"
              @click="toggleSort('priority')"
            >
              <span class="cases-col-head-label">Priority</span>
              <Icon class="cases-col-head-icon" :icon="getSortIcon('priority')" />
            </button>
          </th>
          <th class="cases-col-head is-sortable" :class="{ 'is-active': sortBy === 'newActivity' }">
            <button
              class="cases-col-head-button"
              type="button"
              @click="toggleSort('newActivity')"
            >
              <span class="cases-col-head-label">New Activity</span>
              <Icon class="cases-col-head-icon" :icon="getSortIcon('newActivity')" />
            </button>
          </th>
          <th class="cases-col-head is-sortable" :class="{ 'is-active': sortBy === 'updated' }">
            <button
              class="cases-col-head-button"
              type="button"
              @click="toggleSort('updated')"
            >
              <span class="cases-col-head-label">Updated</span>
              <Icon class="cases-col-head-icon" :icon="getSortIcon('updated')" />
            </button>
          </th>
          <th class="cases-col-head is-sortable" :class="{ 'is-active': sortBy === 'created' }">
            <button
              class="cases-col-head-button"
              type="button"
              @click="toggleSort('created')"
            >
              <span class="cases-col-head-label">Created</span>
              <Icon class="cases-col-head-icon" :icon="getSortIcon('created')" />
            </button>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="caseItem in sortedCases" :key="caseItem.id" class="cases-row">
          <td class="case-subject-cell">
            <TextLink
              class="case-subject-link"
              :to="caseDetailLink(caseItem)"
              no-underline
            >
              <span class="case-subject">{{ caseItem.subject || '-' }}</span>
            </TextLink>
          </td>
          <td class="case-client-cell">
            <router-link
              v-if="hasClientEntity(caseItem)"
              :to="entityDetailLink(caseItem)"
            >
              {{ getClientName(caseItem) }}
            </router-link>
            <span v-else class="case-client-empty">No Client</span>
          </td>
          <td>{{ caseItem.case_number || '-' }}</td>
          <td>
            <Badge :status="getCaseStatusBadgeStatus(caseItem.status)" resizable>
              {{ caseItem.status || '-' }}
            </Badge>
          </td>
          <td>
            <Badge :status="getCasePriorityBadgeStatus(caseItem.priority)" resizable>
              {{ caseItem.priority || '-' }}
            </Badge>
          </td>
          <td>
            <Badge v-if="getUnreadActivityCount(caseItem) > 0" status="danger" resizable>
              {{ getUnreadActivityCount(caseItem) }}
            </Badge>
            <span v-else>-</span>
          </td>
          <td class="case-timestamp-cell">
            <span class="case-timestamp-date">{{ getUpdatedDateParts(caseItem).date }}</span>
            <span
              v-if="getUpdatedDateParts(caseItem).time"
              class="case-timestamp-time"
            >
              {{ getUpdatedDateParts(caseItem).time }}
            </span>
          </td>
          <td class="case-timestamp-cell">
            <span class="case-timestamp-date">{{ getCreatedDateParts(caseItem).date }}</span>
            <span
              v-if="getCreatedDateParts(caseItem).time"
              class="case-timestamp-time"
            >
              {{ getCreatedDateParts(caseItem).time }}
            </span>
          </td>
        </tr>
      </tbody>
      </table>
      <div v-if="hasMore" class="cases-load-more stk-pl-6 stk-pt-4 stk-mb-9">
        <Button
          type="secondary"
          :disabled="isLoadingCases || isLoadingMore"
          @click="loadMoreCases"
        >
          {{ isLoadingMore ? 'Loading...' : 'Load more cases' }}
        </Button>
      </div>
    </template>
  </Stack>
</template>

<script>
import {
  Badge,
  Button,
  Buttons,
  Icon,
  Level,
  LevelLeft,
  LevelRight,
  LoaderWithText,
  Stack,
  StakeModal,
  TextLink,
  Title,
} from '@stakeui';
import {
  getCasePriorityBadgeStatus,
  getCaseStatusBadgeStatus,
} from '@frontend/ui/features/example/utils/caseBadges';
import * as Sentry from '@sentry/vue';

const CASES_HOME_FILTERS_STORAGE_KEY = 'cases_home_filters_v1';

export default {
  name: 'CasesHome',
  components: {
    Badge,
    Button,
    Buttons,
    Icon,
    Level,
    LevelLeft,
    LevelRight,
    LoaderWithText,
    Stack,
    StakeModal,
    TextLink,
    Title,
  },
  data() {
    return {
      cases: [],
      isLoadingCases: false,
      activityBatchToken: 0,
      searchQuery: '',
      subjectSearch: '',
      selectedStatus: '',
      selectedPriority: '',
      showCasePeopleReviewModal: false,
      isLoadingCasePeopleReview: false,
      isApplyingCasePeopleReview: false,
      casePeopleReviewError: '',
      casePeopleApplyError: '',
      casePeopleApplyTaskId: '',
      casePeopleApplyTaskStatus: '',
      casePeopleApplyTaskMeta: {},
      casePeopleApplyPollTimer: null,
      reviewPeople: [],
      reviewCases: [],
      nextCursor: '',
      hasMore: false,
      pageSize: 50,
      bufferedCases: [],
      isLoadingMore: false,
      selectedReviewCaseFilter: 'all',
      reviewSearchQuery: '',
      sortBy: 'updated',
      sortDir: 'desc',
      isRestoringFilters: false,
      savedFiltersSnapshot: null,
    };
  },
  watch: {
    selectedStatus() {
      if (this.isRestoringFilters) {
        return;
      }
      this.persistFilters();
      this.fetchCases();
    },
    selectedPriority() {
      if (this.isRestoringFilters) {
        return;
      }
      this.persistFilters();
      this.fetchCases();
    },
  },
  computed: {
    statusOptions() {
      return [
        {
          label: 'Open',
          value: 'open',
        },
        {
          label: 'Closed',
          value: 'closed',
        },
      ];
    },
    priorityOptions() {
      return [...new Set(this.cases.map(caseItem => caseItem.priority).filter(Boolean))].sort((a, b) => a.localeCompare(b));
    },
    showFilteredCount() {
      return this.cases.length > 0 && (
        Boolean(this.subjectSearch.trim()) ||
        Boolean(this.selectedStatus) ||
        Boolean(this.selectedPriority) ||
        this.sortBy !== 'updated' ||
        this.sortDir !== 'desc'
      );
    },
    isUsingSavedFilter() {
      if (!this.savedFiltersSnapshot) {
        return false;
      }
      return JSON.stringify(this.buildPersistedFilters()) === JSON.stringify(this.savedFiltersSnapshot);
    },
    reviewSummary() {
      return {
        total_cases: this.reviewCases.length,
        actionable_cases: this.reviewCases.filter(caseReview => this.isReviewCaseActionable(caseReview)).length,
      };
    },
    hasReviewActionableItems() {
      return this.reviewCases.some(caseReview => this.isReviewCaseActionable(caseReview));
    },
    filteredReviewPeople() {
      return this.reviewPeople.filter(person => this.getCasesForPerson(person).length > 0);
    },
    isCasePeopleApplyTaskRunning() {
      return ['pending', 'started'].includes(String(this.casePeopleApplyTaskStatus || '').toLowerCase());
    },
    areAllReviewCasesSelected() {
      if (this.visibleReviewCases.length === 0) {
        return false;
      }
      return this.visibleReviewCases.every(caseReview => caseReview.selected);
    },
    visibleReviewCases() {
      return this.reviewCases.filter(caseReview => {
        const person = this.reviewPeople.find(reviewPerson => reviewPerson.person_key === caseReview.person_key);
        if (!person) {
          return false;
        }
        return (
          this.isReviewCaseActionable(caseReview) &&
          this.reviewCaseMatchesFilter(caseReview) &&
          this.reviewCaseMatchesSearch(person, caseReview)
        );
      });
    },
    selectedReviewPeopleCount() {
      return this.reviewPeople.filter(person => this.getCasesForPerson(person).some(caseReview => caseReview.selected)).length;
    },
    selectedReviewCasesCount() {
      return this.reviewCases.filter(caseReview => caseReview.selected).length;
    },
    sortedCases() {
      return this.cases;
    },
  },
  created() {
    this.restoreFilters();
    this.fetchCases();
  },
  beforeDestroy() {
    this.stopCasePeopleApplyTaskPolling();
  },
  methods: {
    getCaseStatusBadgeStatus,
    getCasePriorityBadgeStatus,
    buildPersistedFilters() {
      return {
        selectedStatus: String(this.selectedStatus || '').trim(),
        selectedPriority: String(this.selectedPriority || '').trim(),
        sortBy: String(this.sortBy || 'updated').trim(),
        sortDir: String(this.sortDir || 'desc').trim().toLowerCase() === 'asc' ? 'asc' : 'desc',
      };
    },
    hasActivePersistedFilters(filters = {}) {
      return Boolean(String(filters.selectedStatus || '').trim()) ||
        Boolean(String(filters.selectedPriority || '').trim()) ||
        String(filters.sortBy || 'updated').trim() !== 'updated' ||
        String(filters.sortDir || 'desc').trim().toLowerCase() !== 'desc';
    },
    persistFilters() {
      if (typeof window === 'undefined' || !window.localStorage) {
        return;
      }
      try {
        window.localStorage.setItem(CASES_HOME_FILTERS_STORAGE_KEY, JSON.stringify(this.buildPersistedFilters()));
      } catch (error) {
        // Best effort only; ignore browser storage failures.
      }
    },
    restoreFilters() {
      if (typeof window === 'undefined' || !window.localStorage) {
        return;
      }
      try {
        const savedFilters = window.localStorage.getItem(CASES_HOME_FILTERS_STORAGE_KEY);
        if (!savedFilters) {
          return;
        }
        const parsedFilters = JSON.parse(savedFilters);
        if (!parsedFilters || typeof parsedFilters !== 'object') {
          return;
        }
        this.isRestoringFilters = true;
        // Search is session-only, matching the People page: it should not
        // survive a refresh or logout. Status, priority, and sort still restore.
        this.selectedStatus = String(parsedFilters.selectedStatus || '').trim();
        this.selectedPriority = String(parsedFilters.selectedPriority || '').trim();
        this.sortBy = String(parsedFilters.sortBy || 'updated').trim();
        this.sortDir = String(parsedFilters.sortDir || 'desc').trim().toLowerCase() === 'asc' ? 'asc' : 'desc';
        const restoredSnapshot = this.buildPersistedFilters();
        this.savedFiltersSnapshot = this.hasActivePersistedFilters(restoredSnapshot) ? restoredSnapshot : null;
      } catch (error) {
        // Ignore invalid stored payloads.
      } finally {
        this.isRestoringFilters = false;
      }
    },
    normalizeCaseStatus(caseItem) {
      return String(caseItem?.status || '').trim().toLowerCase();
    },
    isClosedCase(caseItem) {
      const normalizedStatus = this.normalizeCaseStatus(caseItem);
      return normalizedStatus === 'closed';
    },
    hasClientEntity(caseItem) {
      return Boolean(caseItem.entity && caseItem.entity.external_id);
    },
    getClientName(caseItem) {
      return caseItem.entity?.name || '';
    },
    getStatusSortValue(caseItem) {
      return (caseItem.status || '').toLowerCase();
    },
    getPrioritySortValue(caseItem) {
      return (caseItem.priority || '').toLowerCase();
    },
    getUnreadActivityCount(caseItem) {
      const parsed = Number(caseItem?.unread_activity_count);
      if (!Number.isFinite(parsed) || parsed <= 0) {
        return 0;
      }
      return Math.floor(parsed);
    },
    getUpdatedTimestamp(caseItem) {
      const value = caseItem.updated_at || caseItem.created_at;
      if (!value) {
        return 0;
      }

      return new Date(value).getTime() || 0;
    },
    getCreatedTimestamp(caseItem) {
      const value = caseItem.created_at;
      if (!value) {
        return 0;
      }

      return new Date(value).getTime() || 0;
    },
    formatCaseUpdatedAt(caseItem) {
      const value = caseItem.updated_at || caseItem.created_at;
      if (!value) {
        return '-';
      }

      const parsedDate = new Date(value);
      if (Number.isNaN(parsedDate.getTime())) {
        return '-';
      }

      return parsedDate.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    formatCaseCreatedAt(caseItem) {
      const value = caseItem.created_at;
      if (!value) {
        return '-';
      }

      const parsedDate = new Date(value);
      if (Number.isNaN(parsedDate.getTime())) {
        return '-';
      }

      return parsedDate.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    getDateParts(value) {
      if (!value) {
        return {
          date: '-',
          time: '',
        };
      }

      const parsedDate = new Date(value);
      if (Number.isNaN(parsedDate.getTime())) {
        return {
          date: '-',
          time: '',
        };
      }

      return {
        date: parsedDate.toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'short',
          day: '2-digit',
        }),
        time: parsedDate.toLocaleTimeString(undefined, {
          hour: '2-digit',
          minute: '2-digit',
        }),
      };
    },
    getUpdatedDateParts(caseItem) {
      return this.getDateParts(caseItem.updated_at || caseItem.created_at);
    },
    getCreatedDateParts(caseItem) {
      return this.getDateParts(caseItem.created_at);
    },
    sortLoadedCasesForDisplay() {
      if (!Array.isArray(this.cases) || this.cases.length < 2) {
        return;
      }
      if (!['updated', 'newActivity'].includes(this.sortBy)) {
        return;
      }
      const direction = this.sortDir === 'asc' ? 1 : -1;
      this.cases = [...this.cases].sort((leftItem, rightItem) => {
        if (this.sortBy === 'newActivity') {
          const leftCount = this.getUnreadActivityCount(leftItem);
          const rightCount = this.getUnreadActivityCount(rightItem);
          if (leftCount !== rightCount) {
            return leftCount > rightCount ? direction : -direction;
          }
        }
        const leftValue = this.getUpdatedTimestamp(leftItem);
        const rightValue = this.getUpdatedTimestamp(rightItem);
        if (leftValue === rightValue) {
          return 0;
        }
        return leftValue > rightValue ? direction : -direction;
      });
    },
    consumeBufferedCases({ append = false, batchToken = 0 } = {}) {
      if (!Array.isArray(this.bufferedCases) || this.bufferedCases.length === 0) {
        return false;
      }
      const pageSize = Math.max(1, Number(this.pageSize) || 10);
      const nextCases = this.bufferedCases.slice(0, pageSize);
      this.bufferedCases = this.bufferedCases.slice(pageSize);
      if (append) {
        this.cases = [...this.cases, ...nextCases];
      } else {
        this.cases = nextCases;
      }
      this.hasMore = Boolean(this.bufferedCases.length > 0 || this.nextCursor);
      this.sortLoadedCasesForDisplay();
      this.fetchUnreadActivityCounts(nextCases, batchToken);
      return true;
    },
    toggleSort(column) {
      if (this.sortBy === column) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
        this.persistFilters();
        this.fetchCases();
        return;
      }

      this.sortBy = column;
      this.sortDir = ['client', 'status', 'priority'].includes(column) ? 'asc' : 'desc';
      this.persistFilters();
      this.fetchCases();
    },
    getSortIcon(column) {
      if (this.sortBy !== column) {
        return column === 'client' ? 'fas fa-arrow-down' : 'fas fa-arrow-up';
      }

      return this.sortDir === 'asc' ? 'fas fa-arrow-down' : 'fas fa-arrow-up';
    },
    handleSearch() {
      this.subjectSearch = this.searchQuery.trim();
      this.persistFilters();
      this.fetchCases();
    },
    handleClear() {
      this.searchQuery = '';
      this.subjectSearch = '';
      this.selectedStatus = '';
      this.selectedPriority = '';
      this.sortBy = 'updated';
      this.sortDir = 'desc';
      this.persistFilters();
      this.fetchCases();
    },
    caseDetailLink(caseItem) {
      if (caseItem.entity && caseItem.entity.external_id) {
        return {
          name: 'entity-cases',
          params: { entityExternalId: caseItem.entity.external_id },
          query: { caseId: caseItem.id },
        };
      }

      return {
        name: 'case-detail',
        params: { caseId: caseItem.id },
      };
    },
    entityDetailLink(caseItem) {
      return {
        name: 'entity-detail',
        params: { entityExternalId: caseItem.entity.external_id },
      };
    },
    async fetchCases({ importMode = 'cases_only', append = false } = {}) {
      if (!append) {
        this.activityBatchToken += 1;
      }
      const batchToken = this.activityBatchToken;
      if (append && this.consumeBufferedCases({ append: true, batchToken })) {
        return;
      }
      if (append) {
        this.isLoadingMore = true;
      } else {
        this.isLoadingCases = true;
      }
      try {
        const mode = ['cases_only', 'link_existing', 'create_missing'].includes(importMode) ? importMode : 'cases_only';
        const params = new URLSearchParams();
        if (mode !== 'cases_only') {
          params.set('import_mode', mode);
        } else {
          if (this.subjectSearch.trim()) {
            params.set('search', this.subjectSearch.trim());
          }
          if (this.selectedStatus) {
            params.set('status', this.selectedStatus);
          }
          if (this.selectedPriority) {
            params.set('priority', this.selectedPriority);
          }
          params.set('sort_by', this.sortBy);
          params.set('sort_dir', this.sortDir);
          params.set('page_size', String(this.pageSize));
          if (append && this.nextCursor) {
            params.set('cursor', this.nextCursor);
          }
        }
        const queryString = params.toString();
        const url = queryString ? `/api/v1/cases/?${queryString}` : '/api/v1/cases/';
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error('Failed to fetch cases');
        }
        const data = await response.json();
        if (batchToken !== this.activityBatchToken) {
          return;
        }
        const fetchedCases = (data.cases || []).map(caseItem => ({
          ...caseItem,
          unread_activity_count: 0,
        }));
        this.nextCursor = data.next_cursor || '';
        let bufferedCases = fetchedCases;
        if (mode === 'cases_only' && this.sortBy === 'newActivity') {
          const unreadActivityCounts = await this.fetchUnreadActivityCountsForCases(
            fetchedCases,
            batchToken,
          );
          if (batchToken !== this.activityBatchToken) {
            return;
          }
          bufferedCases = fetchedCases.map(caseItem => {
            const caseId = String(caseItem.id || '');
            const unreadCount = Number(unreadActivityCounts[caseId]);
            return {
              ...caseItem,
              unread_activity_count: Number.isFinite(unreadCount) && unreadCount > 0 ? Math.floor(unreadCount) : 0,
            };
          });
          bufferedCases.sort((leftItem, rightItem) => {
            const leftCount = this.getUnreadActivityCount(leftItem);
            const rightCount = this.getUnreadActivityCount(rightItem);
            if (leftCount !== rightCount) {
              return this.sortDir === 'asc' ?
                leftCount - rightCount :
                rightCount - leftCount;
            }
            const leftUpdated = this.getUpdatedTimestamp(leftItem);
            const rightUpdated = this.getUpdatedTimestamp(rightItem);
            return this.sortDir === 'asc' ?
              leftUpdated - rightUpdated :
              rightUpdated - leftUpdated;
          });
        }
        this.bufferedCases = bufferedCases;
        this.consumeBufferedCases({ append, batchToken });
        this.hasMore = Boolean(this.bufferedCases.length > 0 || (data.has_more && this.nextCursor));
      } catch (error) {
        console.error(error);
        Sentry.captureException(error);
      } finally {
        if (!append && batchToken === this.activityBatchToken) {
          this.isLoadingCases = false;
        }
        this.isLoadingMore = false;
      }
    },
    loadMoreCases() {
      if (!this.hasMore || this.isLoadingMore || this.isLoadingCases) {
        return;
      }
      this.fetchCases({ append: true });
    },
    async fetchUnreadActivityCountsForCases(cases, batchToken) {
      if (!Array.isArray(cases) || cases.length === 0) {
        return {};
      }

      const params = new URLSearchParams();
      cases.filter(caseItem => !this.isClosedCase(caseItem)).forEach(caseItem => {
        const caseId = (caseItem && caseItem.id) ? String(caseItem.id).trim() : '';
        if (caseId) {
          params.append('case_id', caseId);
        }
      });
      if (!params.toString()) {
        return {};
      }

      try {
        const response = await fetch(`/api/v1/cases/activity-counts/?${params.toString()}`);
        if (!response.ok) {
          throw new Error('Failed to fetch case activity counts');
        }
        const data = await response.json();
        if (batchToken !== this.activityBatchToken) {
          return {};
        }
        return data.unread_activity_counts || {};
      } catch (error) {
        console.error(error);
        Sentry.captureException(error);
        return {};
      }
    },
    async fetchUnreadActivityCounts(cases, batchToken) {
      const unreadActivityCounts = await this.fetchUnreadActivityCountsForCases(
        cases,
        batchToken,
      );
      if (batchToken !== this.activityBatchToken) {
        return;
      }
      this.cases = this.cases.map(caseItem => {
        const caseId = String(caseItem.id || '');
        if (!Object.prototype.hasOwnProperty.call(unreadActivityCounts, caseId)) {
          return caseItem;
        }
        const unreadCount = Number(unreadActivityCounts[caseId]);
        return {
          ...caseItem,
          unread_activity_count: Number.isFinite(unreadCount) && unreadCount > 0 ? Math.floor(unreadCount) : 0,
        };
      });
      this.sortLoadedCasesForDisplay();
      if (this.sortBy !== 'newActivity' || !this.bufferedCases.length) {
        return;
      }
      this.bufferedCases = this.bufferedCases.map(caseItem => {
        const caseId = String(caseItem.id || '');
        if (!Object.prototype.hasOwnProperty.call(unreadActivityCounts, caseId)) {
          return caseItem;
        }
        const unreadCount = Number(unreadActivityCounts[caseId]);
        return {
          ...caseItem,
          unread_activity_count: Number.isFinite(unreadCount) && unreadCount > 0 ? Math.floor(unreadCount) : 0,
        };
      });
      this.bufferedCases.sort((leftItem, rightItem) => {
        const leftCount = this.getUnreadActivityCount(leftItem);
        const rightCount = this.getUnreadActivityCount(rightItem);
        if (leftCount !== rightCount) {
          return this.sortDir === 'asc' ?
            leftCount - rightCount :
            rightCount - leftCount;
        }
        const leftUpdated = this.getUpdatedTimestamp(leftItem);
        const rightUpdated = this.getUpdatedTimestamp(rightItem);
        return this.sortDir === 'asc' ?
          leftUpdated - rightUpdated :
          rightUpdated - leftUpdated;
      });
    },
    startNewCase() {
      this.$router.push({ name: 'case-create' });
    },
    isReviewCaseActionable(caseReview) {
      return ['link_existing', 'create_missing'].includes(caseReview?.proposed_action);
    },
    getCasesForPerson(person) {
      return this.reviewCases.filter(caseReview => (
        caseReview.person_key === person.person_key &&
        this.isReviewCaseActionable(caseReview) &&
        this.reviewCaseMatchesFilter(caseReview) &&
        this.reviewCaseMatchesSearch(person, caseReview)
      ));
    },
    reviewCaseMatchesFilter(caseReview) {
      if (this.selectedReviewCaseFilter === 'all') {
        return true;
      }
      return caseReview?.proposed_action === this.selectedReviewCaseFilter;
    },
    reviewCaseMatchesSearch(person, caseReview) {
      const normalizedQuery = String(this.reviewSearchQuery || '').trim().toLowerCase();
      if (!normalizedQuery) {
        return true;
      }
      const personText = [
        person?.display_name,
        person?.email,
      ].filter(Boolean).join(' ').toLowerCase();
      const caseText = [
        caseReview?.subject,
        caseReview?.case_number,
        caseReview?.status,
        caseReview?.proposed_reason,
      ].filter(Boolean).join(' ').toLowerCase();
      return personText.includes(normalizedQuery) || caseText.includes(normalizedQuery);
    },
    canToggleCaseForPerson(person, caseReview) {
      if (!this.isReviewCaseActionable(caseReview)) {
        return false;
      }
      if (person.action === 'create_missing') {
        return person.selected;
      }
      return true;
    },
    hydrateCasePeopleReview(reviewPayload) {
      const people = Array.isArray(reviewPayload?.people) ? reviewPayload.people : [];
      const cases = Array.isArray(reviewPayload?.cases) ? reviewPayload.cases : [];
      this.reviewPeople = people.map(person => ({
        ...person,
        selected: false,
        case_ids: Array.isArray(person.case_ids) ? person.case_ids : [],
      }));
      this.reviewCases = cases.map(caseReview => ({
        ...caseReview,
        selected: false,
      }));
      this.reviewPeople.forEach(person => {
        if (person.action !== 'create_missing' || person.selected) {
          return;
        }
        this.reviewCases = this.reviewCases.map(caseReview => {
          if (caseReview.person_key !== person.person_key || !this.isReviewCaseActionable(caseReview)) {
            return caseReview;
          }
          return {
            ...caseReview,
            selected: false,
          };
        });
      });
    },
    toggleReviewPerson(personKey, isSelected) {
      this.reviewPeople = this.reviewPeople.map(person => {
        if (person.person_key !== personKey) {
          return person;
        }
        return {
          ...person,
          selected: isSelected,
        };
      });
      this.reviewCases = this.reviewCases.map(caseReview => {
        if (caseReview.person_key !== personKey || !this.isReviewCaseActionable(caseReview)) {
          return caseReview;
        }
        return {
          ...caseReview,
          selected: isSelected,
        };
      });
    },
    toggleReviewCase(caseId, isSelected) {
      this.reviewCases = this.reviewCases.map(caseReview => {
        if (caseReview.case_id !== caseId || !this.isReviewCaseActionable(caseReview)) {
          return caseReview;
        }
        const person = this.reviewPeople.find(reviewPerson => reviewPerson.person_key === caseReview.person_key);
        if (person && !this.canToggleCaseForPerson(person, caseReview)) {
          return caseReview;
        }
        return {
          ...caseReview,
          selected: isSelected,
        };
      });
    },
    toggleSelectAllReview() {
      const shouldSelectAll = !this.areAllReviewCasesSelected;
      const visibleCaseIds = new Set(this.visibleReviewCases.map(caseReview => caseReview.case_id));
      this.reviewCases = this.reviewCases.map(caseReview => {
        if (!visibleCaseIds.has(caseReview.case_id)) {
          return caseReview;
        }
        return {
          ...caseReview,
          selected: shouldSelectAll,
        };
      });
      this.syncCreateMissingPersonSelections();
    },
    syncCreateMissingPersonSelections() {
      this.reviewPeople = this.reviewPeople.map(person => {
        if (person.action !== 'create_missing') {
          return person;
        }
        const hasSelectedCases = this.reviewCases.some(caseReview => (
          caseReview.person_key === person.person_key &&
          this.isReviewCaseActionable(caseReview) &&
          caseReview.selected
        ));
        return {
          ...person,
          selected: hasSelectedCases,
        };
      });
    },
    async fetchCasePeopleReview() {
      this.isLoadingCasePeopleReview = true;
      this.casePeopleReviewError = '';
      this.casePeopleApplyError = '';
      try {
        const response = await fetch('/api/v1/cases/import/people-review/');
        if (!response.ok) {
          throw new Error('Failed to load case people review');
        }
        const data = await response.json();
        this.hydrateCasePeopleReview(data.review || {});
      } catch (error) {
        console.error(error);
        Sentry.captureException(error);
        this.casePeopleReviewError = 'Failed to load review details. Please try again.';
      } finally {
        this.isLoadingCasePeopleReview = false;
      }
    },
    openCasePeopleReviewModal() {
      this.showCasePeopleReviewModal = true;
      this.fetchCasePeopleReview();
    },
    closeCasePeopleReviewModal() {
      if (this.isApplyingCasePeopleReview) {
        return;
      }
      this.showCasePeopleReviewModal = false;
      this.reviewSearchQuery = '';
      this.selectedReviewCaseFilter = 'all';
      this.casePeopleApplyError = '';
      this.stopCasePeopleApplyTaskPolling();
      this.casePeopleApplyTaskId = '';
      this.casePeopleApplyTaskStatus = '';
      this.casePeopleApplyTaskMeta = {};
    },
    buildSelectedCaseActionsPayload() {
      return this.reviewCases
        .filter(caseReview => caseReview.selected && this.isReviewCaseActionable(caseReview))
        .map(caseReview => ({
          case_id: caseReview.case_id,
          action: caseReview.proposed_action,
        }));
    },
    async applyCasePeopleReviewSelections() {
      const selectedCaseActions = this.buildSelectedCaseActionsPayload();
      if (selectedCaseActions.length === 0) {
        this.casePeopleApplyError = 'Select at least one case to apply changes.';
        return;
      }

      this.isApplyingCasePeopleReview = true;
      this.casePeopleApplyError = '';
      try {
        const response = await fetch('/api/v1/cases/import/people-review/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.$root.$data.csrfToken,
          },
          body: JSON.stringify({
            selected_case_actions: selectedCaseActions,
          }),
        });
        if (!response.ok) {
          throw new Error('Failed to apply case people import review');
        }
        const data = await response.json();
        const task = data.task;
        if (task && task.id) {
          this.casePeopleApplyTaskId = task.id;
          this.casePeopleApplyTaskStatus = task.status || 'pending';
          this.casePeopleApplyTaskMeta = task.meta || {};
          this.startCasePeopleApplyTaskPolling(task.id);
          return;
        }
        await this.fetchCases({ importMode: 'cases_only' });
      } catch (error) {
        console.error(error);
        Sentry.captureException(error);
        this.casePeopleApplyError = 'Failed to apply selected changes. Please try again.';
      } finally {
        this.isApplyingCasePeopleReview = false;
      }
    },
    startCasePeopleApplyTaskPolling(taskId) {
      this.stopCasePeopleApplyTaskPolling();
      if (!taskId) {
        return;
      }
      this.casePeopleApplyPollTimer = setInterval(() => {
        this.pollCasePeopleApplyTaskStatus(taskId);
      }, 2000);
      this.pollCasePeopleApplyTaskStatus(taskId);
    },
    stopCasePeopleApplyTaskPolling() {
      if (this.casePeopleApplyPollTimer) {
        clearInterval(this.casePeopleApplyPollTimer);
        this.casePeopleApplyPollTimer = null;
      }
    },
    async pollCasePeopleApplyTaskStatus(taskId) {
      try {
        const response = await fetch(`/api/v1/tasks/${taskId}/`, {
          method: 'GET',
          headers: {
            'X-CSRFToken': this.$root.$data.csrfToken,
            'Content-Type': 'application/json',
          },
        });
        if (!response.ok) {
          throw new Error('Failed to fetch task status');
        }
        const data = await response.json();
        const task = data.task || {};
        this.casePeopleApplyTaskStatus = task.status || '';
        this.casePeopleApplyTaskMeta = task.meta || {};

        if (this.casePeopleApplyTaskStatus === 'complete') {
          this.stopCasePeopleApplyTaskPolling();
          this.showCasePeopleReviewModal = false;
          this.casePeopleApplyTaskId = '';
          this.casePeopleApplyTaskStatus = '';
          this.casePeopleApplyTaskMeta = {};
          await this.fetchCases({ importMode: 'cases_only' });
          return;
        }

        if (this.casePeopleApplyTaskStatus === 'failed') {
          this.stopCasePeopleApplyTaskPolling();
          this.casePeopleApplyError = this.casePeopleApplyTaskMeta?.error || 'Case linking failed while running in background.';
        }
      } catch (error) {
        this.stopCasePeopleApplyTaskPolling();
        console.error(error);
        Sentry.captureException(error);
        this.casePeopleApplyError = 'Failed to check background task status. Please refresh and try again.';
      }
    },
  },
};
</script>

<style scoped>
@import '../styles/cases-shared.css';

.cases-table {
  border-bottom: 1px solid var(--stk-text-85);
  border-collapse: collapse;
  width: 100%;
}

.search-form {
  align-items: center;
  display: flex;
  gap: 0.75em;
  width: 50%;
}

.cases-controls {
  align-items: center;
  display: flex;
  gap: 0.75em;
  justify-content: space-between;
}

.cases-actions {
  align-items: center;
  display: flex;
  gap: 0.75em;
}

.cases-filters {
  display: flex;
  gap: 0.75em;
}

.case-filter-select {
  min-width: 10rem;
}

.case-filter-select:hover {
  cursor: pointer;
}

.cases-filtered-count {
  color: var(--stk-text-normal);
  font-size: 0.875rem;
  margin: 0;
}

.cases-saved-filter-indicator {
  color: var(--stk-danger-40);
  font-size: 0.8125rem;
  font-weight: 600;
  margin-left: 0.5rem;
}

.cases-saved-filter-link {
  color: inherit;
  text-decoration: underline;
}

.review-panel {
  background: var(--stk-background);
  border: 1px solid var(--stk-color-border);
  border-radius: 0.375rem;
  max-height: 28rem;
  overflow: auto;
  padding: 0.75rem;
}

.review-bulk-actions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.review-filter-controls {
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.review-filter-buttons {
  margin-right: 0;
}

.review-filter-label {
  color: var(--stk-text-60);
  font-size: 0.75rem;
  font-weight: 600;
  margin: 0;
}

.review-search-input {
  max-width: 24rem;
  min-width: 18rem;
  width: 100%;
}

:deep(.review-filter-button) {
  font-size: 0.75rem;
  min-height: 1.85rem;
  padding: 0.2rem 0.55rem;
}

.review-select-all-row {
  display: flex;
  justify-content: flex-end;
}

.review-task-progress {
  background: var(--stk-background);
  border: 1px solid var(--stk-color-border);
  border-radius: 0.375rem;
  padding: 0.75rem;
}

.review-task-progress-title {
  color: var(--stk-text-strong);
  font-size: 0.875rem;
  font-weight: 600;
  margin: 0 0 0.35rem;
}

.review-task-progress-item {
  color: var(--stk-text-normal);
  font-size: 0.8125rem;
  margin: 0.2rem 0 0;
}

.review-panel-title {
  color: var(--stk-text-strong);
  font-size: 0.95rem;
  margin: 0 0 0.75rem;
}

.review-empty {
  color: var(--stk-text-normal);
  font-size: 0.875rem;
  margin: 0;
}

.review-person-row,
.review-case-row {
  border-bottom: 1px solid var(--stk-color-border);
  padding: 0.5rem 0;
}

.review-person-row:last-child {
  border-bottom: 0;
}

.review-checkbox-label {
  align-items: center;
  color: var(--stk-text-strong);
  display: flex;
  font-weight: 500;
  gap: 0.5rem;
}

.review-person-name,
.review-case-subject {
  display: inline-block;
}

.review-action-pill {
  background-color: var(--stk-primary-95);
  border-radius: 9999px;
  color: var(--stk-primary-40);
  display: inline-block;
  font-size: 0.75rem;
  margin-top: 0.35rem;
  padding: 0.1rem 0.5rem;
}

.review-cases-under-person {
  border-left: 2px solid var(--stk-color-border);
  margin-left: 0.75rem;
  margin-top: 0.5rem;
  padding-left: 0.75rem;
}

.review-person-email,
.review-person-cases,
.review-case-meta,
.review-case-reason,
.review-summary {
  color: var(--stk-text-normal);
  font-size: 0.8125rem;
  margin: 0.25rem 0 0;
}

.review-case-reason {
  color: var(--stk-text-60);
}

.review-error {
  color: var(--stk-danger-40);
  font-size: 0.875rem;
  margin: 0;
}

.search-input {
  flex: 1;
  min-width: 0;
}

.cases-head {
  background-color: hsl(var(--stk-text-h) var(--stk-text-s) 98%);
  border-top: 1px solid var(--stk-text-85);
  position: sticky;
  top: 0;
  z-index: 3;
}

.cases-col-head {
  background-color: hsl(var(--stk-text-h) var(--stk-text-s) 98%);
  color: var(--stk-text-60);
  font-size: 0.75em;
  font-weight: 500;
  padding: 0.25rem 0.75rem;
  text-align: left;
}

.cases-col-head.is-sortable {
  color: var(--stk-primary);
  padding: 0;
}

.cases-col-head.is-sortable.is-active {
  background-color: var(--stk-primary-90);
  border-top: 1px solid var(--stk-primary-50);
  color: var(--stk-primary-40);
}

.cases-col-head-button {
  align-items: center;
  background: none;
  border: 0;
  color: inherit;
  cursor: pointer;
  display: flex;
  font: inherit;
  padding: 0.25rem 0.75rem;
  width: 100%;
  margin-left: 5px;
}

.cases-head tr th.cases-col-head:not(:first-child) {
  padding-left: 0;
}

.cases-head tr th.cases-col-head.is-sortable:not(:first-child) .cases-col-head-button {
  padding-left: 0;
}

.cases-col-head-label {
  flex-grow: 1;
  text-align: left;
}

.cases-col-head-icon {
  opacity: 0;
  transition-duration: var(--stk-transition-duration);
  transition-property: opacity;
}

.cases-col-head.is-sortable:hover .cases-col-head-icon,
.cases-col-head.is-sortable.is-active .cases-col-head-icon {
  opacity: 1;
}

.cases-row {
  border-top: 1px solid var(--stk-text-90);
  transition-duration: var(--stk-transition-duration);
  transition-property: background-color;
}

.cases-row:hover {
  background-color: var(--stk-background);
}

.case-subject-cell {
  font-weight: 600;
  padding: 0;
}

.case-client-cell {
  padding: 0;
}

.case-client-cell a {
  color: var(--stk-text-strong);
}

.case-subject-link {
  color: inherit;
  display: block;
  padding: 0.75rem 0.75rem;
  text-decoration: none;
}

.case-client-link {
  color: inherit;
  display: block;
  padding: 0.75rem 0.75rem;
  text-decoration: none;
}

.case-subject {
  color: var(--stk-text-strong);
  position: relative;
}

.case-client {
  color: var(--stk-text-strong);
  font-weight: 600;
  position: relative;
}

.case-subject::after {
  background-color: currentColor;
  content: '';
  height: 1px;
  left: 0;
  position: absolute;
  top: 100%;
  transform: scaleX(0);
  transform-origin: left center;
  transition-duration: calc(2 * var(--stk-transition-duration));
  transition-property: transform;
  width: 100%;
}

.case-client::after {
  background-color: currentColor;
  content: '';
  height: 1px;
  left: 0;
  position: absolute;
  top: 100%;
  transform: scaleX(0);
  transform-origin: left center;
  transition-duration: calc(2 * var(--stk-transition-duration));
  transition-property: transform;
  width: 100%;
}

.cases-row:hover .case-subject::after {
  transform: none;
}

.cases-row:hover .case-client::after {
  transform: none;
}

.case-client-empty {
  color: var(--stk-text-normal);
  display: block;
  font-weight: 400;
  padding: 0.75rem 0.75rem;
}

.cases-table th,
.cases-table td {
  text-align: left;
  border-bottom: 1px solid var(--stk-color-border);
  vertical-align: middle;
}

.case-timestamp-cell {
  color: var(--stk-text-normal);
  font-size: 0.8125rem;
  line-height: 1.1;
}

.case-timestamp-date {
  color: var(--stk-text-normal);
  display: block;
  font-weight: 500;
}

.case-timestamp-time {
  color: var(--stk-text-60);
  display: block;
  font-size: 0.75rem;
  margin-top: 0.15rem;
}

.note {
  color: var(--stk-color-text-muted);
}
</style>
